// ─── Camera ───────────────────────────────────────────────────────────────────

import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  TILE_SIZE,
  type Rect,
} from "./constants";

const TARGET_TILES_WIDE = 40;

/**
 * Per-frame follow smoothing factor. ~0.15 reads as a gentle Zelda-style
 * trail without feeling laggy. Owned here (not constants.ts) so the camera
 * always smooth-follows even if an external constant is zeroed out.
 */
const DEFAULT_CAMERA_LERP = 0.15;

/** Below this distance (world px) the camera snaps to rest — kills sub-pixel jitter. */
const SNAP_EPSILON = 0.5;

export class Camera {
  x: number = 0;
  y: number = 0;
  zoom: number = 1;
  private worldWidth: number;
  private worldHeight: number;
  private viewWidth: number;
  private viewHeight: number;
  private lerp: number;

  constructor(
    worldWidth: number,
    worldHeight: number,
    viewWidth: number = CANVAS_WIDTH,
    viewHeight: number = CANVAS_HEIGHT,
    lerp: number = DEFAULT_CAMERA_LERP,
  ) {
    this.worldWidth = worldWidth;
    this.worldHeight = worldHeight;
    this.viewWidth = viewWidth;
    this.viewHeight = viewHeight;
    // A lerp of 0 would freeze the camera; anything out of (0, 1] falls back.
    this.lerp = lerp > 0 && lerp <= 1 ? lerp : DEFAULT_CAMERA_LERP;
    this.zoom = this._computeZoom(viewWidth);
  }

  private _computeZoom(viewWidth: number): number {
    return Math.max(2, Math.floor(viewWidth / (TARGET_TILES_WIDE * TILE_SIZE)));
  }

  /** Visible world width in world-space pixels. */
  get visibleWorldWidth(): number {
    return this.viewWidth / this.zoom;
  }

  /** Visible world height in world-space pixels. */
  get visibleWorldHeight(): number {
    return this.viewHeight / this.zoom;
  }

  /**
   * Update camera to follow a target position with smooth lerp interpolation.
   * Clamps so the camera never shows outside world bounds.
   */
  update(targetX: number, targetY: number): void {
    const desiredX = targetX - this.visibleWorldWidth / 2;
    const desiredY = targetY - this.visibleWorldHeight / 2;

    const dx = desiredX - this.x;
    const dy = desiredY - this.y;

    // Snap when close enough — prevents sub-pixel shimmer while standing still.
    this.x = Math.abs(dx) < SNAP_EPSILON ? desiredX : this.x + dx * this.lerp;
    this.y = Math.abs(dy) < SNAP_EPSILON ? desiredY : this.y + dy * this.lerp;

    this.x = Math.max(0, Math.min(this.x, Math.max(0, this.worldWidth - this.visibleWorldWidth)));
    this.y = Math.max(0, Math.min(this.y, Math.max(0, this.worldHeight - this.visibleWorldHeight)));
  }

  /**
   * Snap camera directly to target (no lerp). Useful for room transitions.
   */
  snapTo(targetX: number, targetY: number): void {
    this.x = targetX - this.visibleWorldWidth / 2;
    this.y = targetY - this.visibleWorldHeight / 2;

    this.x = Math.max(0, Math.min(this.x, Math.max(0, this.worldWidth - this.visibleWorldWidth)));
    this.y = Math.max(0, Math.min(this.y, Math.max(0, this.worldHeight - this.visibleWorldHeight)));
  }

  /**
   * Apply camera transform to the canvas context.
   * Call before drawing world elements.
   */
  apply(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.scale(this.zoom, this.zoom);
    ctx.translate(-Math.round(this.x), -Math.round(this.y));
  }

  /**
   * Restore canvas context after drawing world elements.
   */
  restore(ctx: CanvasRenderingContext2D): void {
    ctx.restore();
  }

  /**
   * Get the visible bounding rectangle in world coordinates.
   */
  getVisibleBounds(): Rect {
    return {
      x: this.x,
      y: this.y,
      width: this.visibleWorldWidth,
      height: this.visibleWorldHeight,
    };
  }

  /**
   * Update world bounds (e.g., on room transition).
   */
  setWorldBounds(width: number, height: number): void {
    this.worldWidth = width;
    this.worldHeight = height;
  }

  /**
   * Update viewport size (e.g., on window resize). Recomputes zoom.
   */
  setViewport(width: number, height: number): void {
    this.viewWidth = width;
    this.viewHeight = height;
    this.zoom = this._computeZoom(width);
  }

  /**
   * Convert screen coordinates to world coordinates.
   */
  screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    return {
      x: screenX / this.zoom + this.x,
      y: screenY / this.zoom + this.y,
    };
  }
}
